import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { Discount } from 'src/features/discounts/utils/types';
import { useDeleteDiscount } from 'src/features/discounts/api/useDeleteDiscount';
import { LIST_DISCOUNTS_RESOURCE } from 'src/features/discounts/api/useListDiscounts';
import { discountsMessages } from 'src/features/discounts/utils/messages';

interface DeleteDiscountFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedDiscount: Discount;
    children?: React.ReactNode;
}

function DeleteDiscountFormModal<TData>({ selectedDiscount, children }: DeleteDiscountFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const { mutate: deleteDiscount } = useDeleteDiscount({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_DISCOUNTS_RESOURCE] });
            setIsModalOpen(false);
            navigate('/discounts');
            toast({
                title: formatMessage(discountsMessages.deleteDiscountSuccess),
                description: formatMessage(discountsMessages.deleteDiscountSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...discountsMessages.deleteDiscount },
                subTitle: {
                    ...discountsMessages.deleteDiscountSubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...discountsMessages.confirmDeleteDiscount },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: discountsMessages.deleteDiscount,
                callbackFn: () => {
                    return (
                        deleteDiscount({
                            discountId: selectedDiscount.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteDiscountFormModal;
